import * as React from 'react';
import { useState, useEffect } from 'react';
import { MessageBarType } from '@fluentui/react';
import { extractEmailData, downloadAttachments, showNotification } from '../../services/officeService';
import { apiClient } from '../../services/apiClient';
import { MONTH_NAMES } from '../../constants';
import { EmailInfo, AttachmentInfo, SharePointFolder, Client } from '../../types';
import { findNodeByPath, ancestorPaths, insertChildAtPath, renameNodeAtPath, removeNodeAtPath, moveNodeToParent } from '../../utils/folderTree';
import { getLastFolder, setLastFolder } from '../../utils/folderPreferences';
import UnauthorizedAccess, { ErrorType } from './UnauthorizedAccess';
import Header from './Header';
import MessageBanner from './MessageBanner';
import ClientSelector from './ClientSelector';
import SharePointFolderPicker from './SharePointFolderPicker';
import SaveOptions from './SaveOptions';
import AttachmentsList from './AttachmentsList';
import ActionButtons from './ActionButtons';
import LoadingOverlay from './LoadingOverlay';
import './App.css';

const App: React.FC = () => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [emailInfo, setEmailInfo] = useState<EmailInfo | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
  const [notes, setNotes] = useState<string>('');
  const [sharepointFolderPath, setSharepointFolderPath] = useState<string>('');
  const [autoFolderPath, setAutoFolderPath] = useState<string>('');
  const [documentsRootUrl, setDocumentsRootUrl] = useState<string>('');
  const [isLoadingSharePoint, setIsLoadingSharePoint] = useState<boolean>(false);
  const [isRefreshingFolders, setIsRefreshingFolders] = useState<boolean>(false);
  const [isFolderOpBusy, setIsFolderOpBusy] = useState<boolean>(false);
  const [message, setMessage] = useState<{ text: string; type: MessageBarType } | null>(null);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [isAuthInitialized, setIsAuthInitialized] = useState<boolean>(false);
  const [attachments, setAttachments] = useState<AttachmentInfo[]>([]);
  const [folderTree, setFolderTree] = useState<SharePointFolder[]>([]);
  const [expandedPaths, setExpandedPaths] = useState<Set<string>>(new Set());
  const [selectedFolder, setSelectedFolder] = useState<string>('');
  const [isOutgoingEmail, setIsOutgoingEmail] = useState<boolean>(false);
  const [saveEmailChecked, setSaveEmailChecked] = useState<boolean>(false);
  const [saveAttachmentsChecked, setSaveAttachmentsChecked] = useState<boolean>(false);
  const [isMessageFading, setIsMessageFading] = useState<boolean>(false);
  const [authError, setAuthError] = useState<{ type: ErrorType; message: string } | null>(null);
  // True when the email-based auto-match returned no klanten. Stays true after a manual
  // pick so the "Aangepast" option remains available for further searches in the same session.
  const [autoMatchFailed, setAutoMatchFailed] = useState<boolean>(false);

  /**
   * Initialize authentication and Graph client on mount
   */
  useEffect(() => {
    initializeAuth();
  }, []);

  /**
   * Initialize - load email data when component mounts
   */
  useEffect(() => {
    if (isAuthInitialized) {
      loadEmailData();
    }
  }, [isAuthInitialized]);

  /**
   * Auto-dismiss message banner after 3 seconds
   */
  useEffect(() => {
    if (message) {
      setIsMessageFading(false);
      const fadeTimer = setTimeout(() => {
        setIsMessageFading(true);
      }, 2700); // Start fade 300ms before removal

      const removeTimer = setTimeout(() => {
        setMessage(null);
        setIsMessageFading(false);
      }, 3000);

      return () => {
        clearTimeout(fadeTimer);
        clearTimeout(removeTimer);
      };
    }
  }, [message]);

  /**
   * Fetch SharePoint folder path when selected client changes
   */
  useEffect(() => {
    const fetchSharePointPath = async () => {
      // Only fetch SharePoint data after authentication is initialized
      if (selectedClientId && isAuthInitialized) {
        // Clear previous folder selection when client changes
        setSelectedFolder('');
        setSharepointFolderPath('');
        setAutoFolderPath('');
        setDocumentsRootUrl('');
        setExpandedPaths(new Set());
        setIsLoadingSharePoint(true);

        try {
          // Fetch both folders list and default path in parallel
          const [foldersResult, result] = await Promise.all([
            apiClient.getClientSharePointFolders(selectedClientId),
            apiClient.getClientSharePointPath(selectedClientId),
          ]);

          // Always set the auto path so it has a valid URL to restore
          const autoPath = result.success ? (result.webUrl || result.folderPath) : '';
          setAutoFolderPath(autoPath);

          let preSelectedMatch = false;
          if (foldersResult.success && foldersResult.folders) {
            setFolderTree(foldersResult.folders);
            setDocumentsRootUrl(foldersResult.documentsRootUrl || '');

            // 1. Restore the user's last-selected folder for this client if it still exists.
            const savedPath = getLastFolder(selectedClientId);
            if (savedPath !== null) {
              if (savedPath === '') {
                setSelectedFolder('');
                setSharepointFolderPath(foldersResult.documentsRootUrl || autoPath);
                preSelectedMatch = true;
              } else {
                const savedNode = findNodeByPath(foldersResult.folders, savedPath);
                if (savedNode) {
                  setSelectedFolder(savedNode.path);
                  setSharepointFolderPath(savedNode.webUrl || autoPath);
                  setExpandedPaths(new Set(ancestorPaths(savedNode.path)));
                  preSelectedMatch = true;
                }
              }
            }

            // 2. Otherwise pre-select the folder matching the email's received year/month.
            // Tree is rooted at the drive root, so the target path is prefixed with the base folder
            // (e.g. "Correspondentie/2026/May") when one is configured server-side.
            if (!preSelectedMatch && emailInfo?.receivedDate) {
              const receivedDate = new Date(emailInfo.receivedDate);
              const mailYear = receivedDate.getFullYear().toString();
              const mailMonth = MONTH_NAMES[receivedDate.getMonth()];
              const baseFolder: string = foldersResult.baseFolder || '';
              const targetPath = baseFolder
                ? `${baseFolder}/${mailYear}/${mailMonth}`
                : `${mailYear}/${mailMonth}`;
              const matchingFolder = findNodeByPath(foldersResult.folders, targetPath);
              if (matchingFolder) {
                setSelectedFolder(matchingFolder.path);
                setSharepointFolderPath(matchingFolder.webUrl || matchingFolder.path);
                setExpandedPaths(new Set(ancestorPaths(targetPath)));
                preSelectedMatch = true;
              }
            }
          }

          if (!preSelectedMatch) {
            setSharepointFolderPath(autoPath);
          }
        } catch (error: any) {
          console.error('Error fetching SharePoint path:', error);
          setSharepointFolderPath('');
          setMessage({
            text: 'Waarschuwing: SharePoint map kon niet worden opgehaald',
            type: MessageBarType.warning,
          });
        } finally {
          setIsLoadingSharePoint(false);
        }
      } else {
        setSharepointFolderPath('');
        setFolderTree([]);
        setExpandedPaths(new Set());
        setSelectedFolder('');
        setDocumentsRootUrl('');
        setIsLoadingSharePoint(false);
      }
    };

    fetchSharePointPath();
  }, [selectedClientId, isAuthInitialized, emailInfo?.receivedDate]);

  /**
   * Initialize authentication using Azure AD SSO
   * This replaces the old JWT token authentication
   */
  const initializeAuth = async () => {
    try {

      // Clear any previous auth errors
      setAuthError(null);

      // Initialize authentication using Azure AD SSO
      const authResult = await apiClient.initializeAuth();
      if (authResult.success) {
        setIsAuthInitialized(true);

        // Initialize Microsoft Graph client (for SharePoint operations)
        try {
          const graphResult = await apiClient.initializeGraphClient();

          if (graphResult.success) {
          } else {
            console.warn('Graph client initialization failed:', graphResult.message);
            // Continue anyway - Graph is only needed for SharePoint operations
          }
        } catch (graphError: any) {
          // If graph initialization throws an auth error, treat it as overall auth failure
          console.error('Graph client auth error:', graphError);
          setIsAuthInitialized(false);

          const errorType: ErrorType = graphError.type || 'AUTH_FAILED';
          const errorMessage = graphError.message || 'Failed to authenticate with Microsoft Graph. Please try again.';

          setAuthError({
            type: errorType,
            message: errorMessage
          });
          return; // Exit early to prevent further processing
        }
      } else {
        console.error('Azure AD authentication failed:', authResult.error);
        setIsAuthInitialized(false);

        // Set auth error for structured error handling
        const errorMsg = authResult.error || 'Authentication failed. Please ensure you are signed in to Outlook with your organization account.';
        setAuthError({
          type: 'AUTH_FAILED',
          message: errorMsg
        });
      }
    } catch (error: any) {
      console.error('Error initializing authentication:', error);
      setIsAuthInitialized(false);

      // Determine error type based on error properties
      let errorType: ErrorType = 'AUTH_FAILED';
      let errorMessage = 'Authentication failed. ';

      // Check if error has a type property (from apiClient or ssoService)
      if (error.type) {
        errorType = error.type;
        errorMessage = error.message;
      } else {
        // Legacy error handling for backwards compatibility
        if (error.message?.includes('not available')) {
          errorMessage += 'Office.js is not properly loaded. Please try reloading the add-in.';
          errorType = 'AUTH_FAILED';
        } else if (error.message?.includes('cancelled')) {
          errorMessage += 'You cancelled the sign-in. Please try again.';
          errorType = 'AUTH_FAILED';
        } else if (error.message?.includes('not from the authorized organization') || error.message?.includes('not authorized')) {
          errorMessage += 'This add-in is only available for authorized organization members.';
          errorType = 'FORBIDDEN';
        } else if (error.message?.includes('expired')) {
          errorMessage += 'Your session has expired. Please sign in again.';
          errorType = 'EXPIRED';
        } else if (error.message?.includes('Network')) {
          errorMessage += 'Network error. Please check your connection and try again.';
          errorType = 'NETWORK';
        } else {
          errorMessage += error.message || 'Please try reloading the add-in.';
        }
      }

      // Set structured auth error
      setAuthError({
        type: errorType,
        message: errorMessage
      });
    }
  };

  /**
   * Handle authentication retry from UnauthorizedAccess component
   */
  const handleAuthRetry = () => {
    setAuthError(null);
    setMessage(null);
    initializeAuth();
  };

  /**
   * Load email data from currently selected email
   */
  const loadEmailData = async (showLoader: boolean = true) => {
    if (showLoader) setIsLoading(true);
    setMessage(null);

    try {
      // Extract email metadata
      const emailData = await extractEmailData();


      // Determine if this is an outgoing email
      const isOutgoing = emailData.isOutgoing || false;
      setIsOutgoingEmail(isOutgoing);

      setEmailInfo({
        subject: emailData.subject,
        senderEmail: emailData.senderEmail,
        senderName: emailData.senderName,
        receivedDate: emailData.receivedDate,
        attachmentCount: emailData.attachmentCount,
        isOutgoing: isOutgoing,
      });
      // Load attachments with selection checkboxes. Reuse the list already
      // extracted by extractEmailData() instead of re-accessing Office.js.
      if (emailData.attachmentCount > 0 && emailData.attachments) {
        const attachmentList: AttachmentInfo[] = emailData.attachments
          .filter(att => att.attachmentType === Office.MailboxEnums.AttachmentType.File)
          .map(att => ({
            id: att.id,
            name: att.name,
            size: att.size,
            selected: false, // Default: all unselected
          }));
        setAttachments(attachmentList);
      }

      // Match email with clients (use recipient for outgoing, sender for incoming)
      let emailToMatch = emailData.senderEmail;

      // For outgoing emails, extract recipient email from TO field
      if (isOutgoing && emailData.toRecipients) {
        // Extract first email from "Name <email@domain.com>" format
        const emailMatch = emailData.toRecipients.match(/<([^>]+)>/);
        if (emailMatch && emailMatch[1]) {
          emailToMatch = emailMatch[1];
        } else {
          // If no angle brackets, try to extract first email-like string
          const simpleEmailMatch = emailData.toRecipients.match(/[\w.-]+@[\w.-]+\.\w+/);
          if (simpleEmailMatch) {
            emailToMatch = simpleEmailMatch[0];
          }
        }
      }

      await matchClients(emailToMatch);
    } catch (error: any) {
      console.error('Error loading email data:', error);
      setMessage({
        text: `Fout bij laden e-mail: ${error.message}`,
        type: MessageBarType.error,
      });
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Match sender email with clients in the system
   */
  const matchClients = async (senderEmail: string) => {
    try {
      const result = await apiClient.getKlantenByEmail(senderEmail);
      if (result.success && result.klanten && result.klanten.length > 0) {
        setClients(result.klanten);
        setAutoMatchFailed(false);

        setMessage({
          text: `${result.count} klant${result.klanten.length > 1 ? 'en' : ''} gevonden. Selecteer de juiste klant.`,
          type: MessageBarType.info,
        });
      } else {
        // No matches found - the "Aangepast" option in the dropdown lets the user search manually.
        setAutoMatchFailed(true);
        setMessage({
          text: 'Geen klant gevonden op basis van het e-mailadres. Kies "Aangepast" om handmatig te zoeken.',
          type: MessageBarType.warning,
        });
      }
    } catch (error: any) {
      console.error('Error matching clients:', error);
      // Treat lookup failure like "no match" so the user can fall back to manual search.
      setAutoMatchFailed(true);
      setMessage({
        text: 'Fout bij zoeken naar klant. Kies "Aangepast" om handmatig te zoeken.',
        type: MessageBarType.warning,
      });
    }
  };

  /**
   * Picked from the manual "Aangepast" search panel. Merge the chosen klant into the
   * dropdown's list so the <select> renders its label, then set it as selected — that
   * triggers the existing SharePoint useEffect just like an auto-matched pick.
   */
  const handleCustomClientPick = (picked: Client) => {
    setClients(prev => prev.some(c => c.id === picked.id) ? prev : [...prev, picked]);
    setSelectedClientId(picked.id);
    setMessage({
      text: `Klant ${picked.klantnaam} geselecteerd.`,
      type: MessageBarType.success,
    });
  };

  /**
   * Handle saving the email
   */
  const handleSaveEmail = async () => {
    // Validation: No client selected
    if (!selectedClientId) {
      setMessage({
        text: 'Selecteer eerst een klant om verder te gaan.',
        type: MessageBarType.error,
      });
      return;
    }

    // Email metadata is always persisted to the DB, regardless of which
    // checkbox is ticked. The checkboxes only control SharePoint side effects:
    //  - "E-mail opslaan"  -> upload .msg to SharePoint  (saveEmailFile below)
    //  - "Bijlagen opslaan" -> upload attachments to SharePoint
    const effectiveSaveEmail = true;
    const effectiveSaveAttachments = saveAttachmentsChecked;

    // Validation: Check if SharePoint folder is available (only if saving attachments)
    if (effectiveSaveAttachments && !sharepointFolderPath) {
      setMessage({
        text: 'De SharePoint-map voor deze klant is niet beschikbaar.',
        type: MessageBarType.error,
      });
      return;
    }

    setIsProcessing(true);
    setMessage(null);

    try {
      // Email metadata is always saved to the DB, so always extract the full payload.
      const emailData: any = await extractEmailData();

      // Download attachments only if we are saving attachments
      let selectedAttachments: any[] = [];
      if (effectiveSaveAttachments) {
        const allAttachments = await downloadAttachments();
        selectedAttachments = allAttachments.filter(att => {
          // Match by original filename since ID may not be preserved
          return attachments.some(a => a.selected && a.name === att.originalFilename);
        });
      }
      const model = {
        clientId: selectedClientId,
        email: emailData,
        attachment: selectedAttachments,
        // Send the user's selection literally — empty string = drive root (Hoofdmap).
        // Backend uploads files directly into this folder.
        customFolder: selectedFolder,
        isOutgoing: isOutgoingEmail,
        // saveEmail controls DB save (true also when neither checkbox is ticked,
        // so we still persist the metadata as a fallback).
        saveEmail: effectiveSaveEmail,
        saveAttachments: effectiveSaveAttachments,
        // saveEmailFile controls SharePoint .msg upload — only when the user
        // explicitly ticks "E-mail opslaan". Neither-checked => DB only, no SharePoint.
        saveEmailFile: saveEmailChecked,
        // Backend uses these to fetch the email MIME via Graph and upload it as
        // <subject>.msg into the user-selected folder.
        restMessageId: emailData.restMessageId || '',
        userPrincipalName: emailData.userPrincipalName || '',
      };

      // Save to backend
      const result = await apiClient.saveEmailAttachments(model);
      if (result.success) {
        const attachmentCount = result.attachmentCount || 0;
        const sharepointUploaded = result.sharepointUploaded || 0;
        const emailFileUploaded = !!result.emailFileUploaded;

        // Determine appropriate success message based on what was saved
        let messageText: string = '';
        let messageType: MessageBarType = MessageBarType.success;
        let notificationMessage: string = '';

        // Email metadata is always saved to DB. Messages are driven by the
        // literal user selections: which SharePoint side effects were requested,
        // and whether they succeeded.
        const wantEmailFile = saveEmailChecked;
        const wantAttachments = saveAttachmentsChecked;
        const allAttachmentsOk = attachmentCount === 0 || sharepointUploaded === attachmentCount;
        const someAttachmentsOk = sharepointUploaded > 0 && sharepointUploaded < attachmentCount;

        if (!wantEmailFile && !wantAttachments) {
          // Neither ticked — DB save only.
          messageText = 'E-mail is opgeslagen in de database.';
          messageType = MessageBarType.success;
          notificationMessage = 'E-mail succesvol opgeslagen';
        } else if (wantEmailFile && !wantAttachments) {
          // Email only — DB + .msg in SharePoint.
          if (emailFileUploaded) {
            messageText = 'E-mail is opgeslagen in de database en als .msg in SharePoint.';
            messageType = MessageBarType.success;
            notificationMessage = 'E-mail succesvol opgeslagen';
          } else {
            messageText = 'E-mail is opgeslagen in de database, maar het .msg-bestand kon niet naar SharePoint worden geüpload.';
            messageType = MessageBarType.warning;
            notificationMessage = 'E-mail opgeslagen met waarschuwing';
          }
        } else if (!wantEmailFile && wantAttachments) {
          // Attachments only — DB + attachments to SharePoint, no .msg.
          if (attachmentCount === 0) {
            messageText = 'E-mail is opgeslagen in de database. Geen bijlagen gevonden.';
            messageType = MessageBarType.success;
            notificationMessage = 'E-mail succesvol opgeslagen';
          } else if (allAttachmentsOk) {
            messageText = `E-mail is opgeslagen. Bijlagen (${attachmentCount}) zijn succesvol opgeslagen in SharePoint.`;
            messageType = MessageBarType.success;
            notificationMessage = 'Bijlagen succesvol opgeslagen';
          } else if (someAttachmentsOk) {
            messageText = `E-mail is opgeslagen. Sommige bijlagen (${sharepointUploaded}/${attachmentCount}) zijn opgeslagen.`;
            messageType = MessageBarType.warning;
            notificationMessage = 'Bijlagen gedeeltelijk opgeslagen';
          } else {
            messageText = 'E-mail is opgeslagen, maar bijlagen konden niet worden geüpload naar SharePoint.';
            messageType = MessageBarType.warning;
            notificationMessage = 'Fout bij uploaden bijlagen';
          }
        } else {
          // Both ticked — DB + .msg + attachments.
          if (emailFileUploaded && allAttachmentsOk && attachmentCount === 0) {
            messageText = 'E-mail is opgeslagen als .msg in SharePoint. Geen bijlagen gevonden.';
            messageType = MessageBarType.success;
            notificationMessage = 'E-mail succesvol opgeslagen';
          } else if (emailFileUploaded && allAttachmentsOk) {
            messageText = 'E-mail en bijlagen zijn succesvol opgeslagen bij de geselecteerde klant.';
            messageType = MessageBarType.success;
            notificationMessage = 'E-mail en bijlagen succesvol opgeslagen';
          } else if (emailFileUploaded && someAttachmentsOk) {
            messageText = 'E-mail is opgeslagen. Sommige bijlagen konden niet worden opgeslagen in SharePoint.';
            messageType = MessageBarType.warning;
            notificationMessage = 'E-mail opgeslagen met waarschuwing';
          } else if (emailFileUploaded) {
            messageText = 'E-mail is opgeslagen, maar bijlagen konden niet worden geüpload naar SharePoint.';
            messageType = MessageBarType.warning;
            notificationMessage = 'E-mail opgeslagen met waarschuwing';
          } else {
            messageText = 'E-mail is opgeslagen in de database, maar het .msg-bestand kon niet naar SharePoint worden geüpload.';
            messageType = MessageBarType.warning;
            notificationMessage = 'E-mail opgeslagen met waarschuwing';
          }
        }

        setMessage({
          text: messageText,
          type: messageType,
        });

        // Email always lands in the DB, so the success path is always
        // success-or-warning — info-toast and form-reset are unconditional.
        showNotification(notificationMessage, 'info');
        resetForm();
      } else {
        throw new Error(result.message || 'Opslaan mislukt');
      }

    } catch (error: any) {
      console.error('Error saving email:', error);

      // Determine specific error message
      let errorMessage = 'Fout bij opslaan: ';

      if (error.response?.status === 404) {
        errorMessage = 'De SharePoint-map voor deze klant is niet beschikbaar.';
      } else if (error.message?.includes('SharePoint')) {
        errorMessage = 'Bijlagen konden niet worden geüpload naar SharePoint.';
      } else {
        errorMessage += error.message || 'Onbekende fout opgetreden';
      }

      setMessage({
        text: errorMessage,
        type: MessageBarType.error,
      });
      showNotification('Fout bij opslaan e-mail', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * Handle cancel
   */
  const handleCancel = () => {
    resetForm();
  };

  /**
   * Reset form to initial state
   */
  const resetForm = (showLoader: boolean = false) => {
    setClients([]);
    setSelectedClientId(null);
    setNotes('');
    setSharepointFolderPath('');
    setIsLoadingSharePoint(false);
    setMessage(null);
    setAttachments([]);
    setFolderTree([]);
    setExpandedPaths(new Set());
    setSelectedFolder('');
    setDocumentsRootUrl('');
    setIsOutgoingEmail(false);
    setSaveEmailChecked(false);
    setSaveAttachmentsChecked(false);
    setAutoMatchFailed(false);
    if (showLoader) setEmailInfo(null);
    loadEmailData(showLoader);
  };

  /**
   * Toggle attachment selection
   */
  const toggleAttachment = (id: string) => {
    setAttachments(prev => prev.map(att =>
      att.id === id ? { ...att, selected: !att.selected } : att
    ));
  };

  /**
   * Toggle all attachments
   */
  const toggleAllAttachments = () => {
    const allSelected = attachments.every(att => att.selected);
    setAttachments(prev => prev.map(att => ({ ...att, selected: !allSelected })));
  };

  /**
   * Tree-based folder picker handlers
   */
  const handleTreeSelect = (node: SharePointFolder) => {
    setSelectedFolder(node.path);
    if (node.path === '') {
      setSharepointFolderPath(documentsRootUrl || autoFolderPath);
    } else {
      setSharepointFolderPath(node.webUrl || autoFolderPath);
    }
    if (selectedClientId !== null) {
      setLastFolder(selectedClientId, node.path);
    }
  };

  const handleTreeToggle = (path: string) => {
    setExpandedPaths(prev => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path); else next.add(path);
      return next;
    });
  };

  /**
   * Create a new SharePoint folder under `parentPath` (empty string = drive root),
   * insert it into the tree, expand the parent, and select the new folder.
   */
  const handleCreateFolder = async (
    parentPath: string,
    folderName: string
  ): Promise<{ success: boolean; error?: string }> => {
    if (!selectedClientId) {
      return { success: false, error: 'Selecteer eerst een klant.' };
    }
    try {
      const result = await apiClient.createSharePointFolder(
        selectedClientId,
        parentPath,
        folderName
      );
      if (!result.success || !result.folder) {
        return { success: false, error: result.message || 'Map kon niet worden aangemaakt.' };
      }
      const newNode: SharePointFolder = {
        name: result.folder.name,
        path: result.folder.path,
        webUrl: result.folder.webUrl,
        children: [],
      };
      setFolderTree(prev => insertChildAtPath(prev, parentPath, newNode));
      setExpandedPaths(prev => {
        const next = new Set(prev);
        ancestorPaths(newNode.path).forEach(p => next.add(p));
        return next;
      });
      setSelectedFolder(newNode.path);
      setSharepointFolderPath(newNode.webUrl || autoFolderPath);
      setLastFolder(selectedClientId, newNode.path);
      return { success: true };
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Map kon niet worden aangemaakt.';
      return { success: false, error: message };
    }
  };

  /**
   * Rename an existing SharePoint folder. Updates the tree node, rewrites
   * descendant paths, and keeps the current selection in sync if needed.
   */
  const handleRenameFolder = async (
    oldPath: string,
    newName: string
  ): Promise<{ success: boolean; error?: string }> => {
    if (!selectedClientId) {
      return { success: false, error: 'Selecteer eerst een klant.' };
    }
    if (!oldPath) {
      return { success: false, error: 'De hoofdmap kan niet worden hernoemd.' };
    }
    try {
      const result = await apiClient.renameSharePointFolder(
        selectedClientId,
        oldPath,
        newName
      );
      if (!result.success || !result.folder) {
        return { success: false, error: result.message || 'Map kon niet worden hernoemd.' };
      }
      const updated = {
        name: result.folder.name as string,
        path: result.folder.path as string,
        webUrl: result.folder.webUrl as string | undefined,
      };
      setFolderTree(prev => renameNodeAtPath(prev, oldPath, updated));
      setExpandedPaths(prev => {
        const next = new Set<string>();
        prev.forEach(p => {
          if (p === oldPath) next.add(updated.path);
          else if (p.startsWith(`${oldPath}/`)) next.add(`${updated.path}${p.slice(oldPath.length)}`);
          else next.add(p);
        });
        return next;
      });
      if (selectedFolder === oldPath || selectedFolder.startsWith(`${oldPath}/`)) {
        const newSelected = selectedFolder === oldPath
          ? updated.path
          : `${updated.path}${selectedFolder.slice(oldPath.length)}`;
        setSelectedFolder(newSelected);
        if (selectedFolder === oldPath) {
          setSharepointFolderPath(updated.webUrl || autoFolderPath);
        }
        setLastFolder(selectedClientId, newSelected);
      }
      return { success: true };
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Map kon niet worden hernoemd.';
      return { success: false, error: message };
    }
  };

  /**
   * Re-fetch the SharePoint folder tree for the current client without
   * tearing down the picker. Preserves the current selection when the
   * folder still exists; otherwise falls back to the drive root.
   */
  const handleRefreshFolders = async () => {
    if (!selectedClientId || !isAuthInitialized || isRefreshingFolders) return;
    setIsRefreshingFolders(true);
    try {
      const [foldersResult, result] = await Promise.all([
        apiClient.getClientSharePointFolders(selectedClientId),
        apiClient.getClientSharePointPath(selectedClientId),
      ]);

      const autoPath = result.success ? (result.webUrl || result.folderPath) : '';
      setAutoFolderPath(autoPath);

      if (foldersResult.success && foldersResult.folders) {
        const newTree = foldersResult.folders;
        const newRootUrl = foldersResult.documentsRootUrl || '';
        setFolderTree(newTree);
        setDocumentsRootUrl(newRootUrl);

        // Drop expanded paths that no longer exist in the refreshed tree.
        setExpandedPaths(prev => {
          const next = new Set<string>();
          prev.forEach(p => {
            if (p === '' || findNodeByPath(newTree, p)) next.add(p);
          });
          return next;
        });

        if (selectedFolder === '') {
          setSharepointFolderPath(newRootUrl || autoPath);
        } else {
          const stillExists = findNodeByPath(newTree, selectedFolder);
          if (stillExists) {
            setSharepointFolderPath(stillExists.webUrl || autoPath);
          } else {
            setSelectedFolder('');
            setSharepointFolderPath(newRootUrl || autoPath);
            setLastFolder(selectedClientId, '');
          }
        }
      }
    } catch (error: any) {
      console.error('Error refreshing SharePoint folders:', error);
      setMessage({
        text: 'SharePoint mappen konden niet worden ververst.',
        type: MessageBarType.warning,
      });
    } finally {
      setIsRefreshingFolders(false);
    }
  };

  /**
   * Permanently delete a SharePoint folder. Removes it (and its descendants)
   * from the tree, prunes expanded paths, and resets the selection to Hoofdmap
   * if the deleted folder was the current selection or one of its ancestors.
   */
  const handleDeleteFolder = async (
    path: string
  ): Promise<{ success: boolean; error?: string }> => {
    if (!selectedClientId) {
      return { success: false, error: 'Selecteer eerst een klant.' };
    }
    if (!path) {
      return { success: false, error: 'De hoofdmap kan niet worden verwijderd.' };
    }
    try {
      const result = await apiClient.deleteSharePointFolder(selectedClientId, path);
      if (!result.success) {
        return { success: false, error: result.message || 'Map kon niet worden verwijderd.' };
      }
      setFolderTree(prev => removeNodeAtPath(prev, path));
      setExpandedPaths(prev => {
        const next = new Set<string>();
        prev.forEach(p => {
          if (p !== path && !p.startsWith(`${path}/`)) next.add(p);
        });
        return next;
      });
      if (selectedFolder === path || selectedFolder.startsWith(`${path}/`)) {
        setSelectedFolder('');
        setSharepointFolderPath(documentsRootUrl || autoFolderPath);
        setLastFolder(selectedClientId, '');
      }
      return { success: true };
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Map kon niet worden verwijderd.';
      return { success: false, error: message };
    }
  };

  /**
   * Move a SharePoint folder under a different parent. Updates the tree node
   * (and all of its descendants), rewrites expanded paths, and keeps the
   * current selection in sync if it was inside the moved subtree.
   */
  const handleMoveFolder = async (
    sourcePath: string,
    newParentPath: string
  ): Promise<{ success: boolean; error?: string }> => {
    if (!selectedClientId) {
      return { success: false, error: 'Selecteer eerst een klant.' };
    }
    if (!sourcePath) {
      return { success: false, error: 'De hoofdmap kan niet worden verplaatst.' };
    }
    if (newParentPath === sourcePath || newParentPath.startsWith(`${sourcePath}/`)) {
      return { success: false, error: 'Een map kan niet naar zichzelf of een submap worden verplaatst.' };
    }
    try {
      const result = await apiClient.moveSharePointFolder(
        selectedClientId,
        sourcePath,
        newParentPath
      );
      if (!result.success || !result.folder) {
        return { success: false, error: result.message || 'Map kon niet worden verplaatst.' };
      }
      const updated = {
        path: result.folder.path as string,
        webUrl: result.folder.webUrl as string | undefined,
      };
      setFolderTree(prev => moveNodeToParent(prev, sourcePath, newParentPath, updated));
      setExpandedPaths(prev => {
        const next = new Set<string>();
        prev.forEach(p => {
          if (p === sourcePath) next.add(updated.path);
          else if (p.startsWith(`${sourcePath}/`)) next.add(`${updated.path}${p.slice(sourcePath.length)}`);
          else next.add(p);
        });
        ancestorPaths(updated.path).forEach(p => next.add(p));
        return next;
      });
      if (selectedFolder === sourcePath || selectedFolder.startsWith(`${sourcePath}/`)) {
        const newSelected = selectedFolder === sourcePath
          ? updated.path
          : `${updated.path}${selectedFolder.slice(sourcePath.length)}`;
        setSelectedFolder(newSelected);
        if (selectedFolder === sourcePath) {
          setSharepointFolderPath(updated.webUrl || autoFolderPath);
        }
        setLastFolder(selectedClientId, newSelected);
      }
      return { success: true };
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Map kon niet worden verplaatst.';
      return { success: false, error: message };
    }
  };

  const dismissMessage = () => {
    setIsMessageFading(true);
    setTimeout(() => {
      setMessage(null);
      setIsMessageFading(false);
    }, 300);
  };

  const hasValidSharePointSite = (): boolean => {
    return !!sharepointFolderPath && sharepointFolderPath.startsWith('https://');
  };

  const isFolderPickerVisible = (): boolean => {
    return (
      !!selectedClientId &&
      !isOutgoingEmail &&
      !!emailInfo &&
      (saveEmailChecked || (saveAttachmentsChecked && (emailInfo?.attachmentCount ?? 0) > 0))
    );
  };

  const isSaveDisabled = (): boolean => {
    if (!selectedClientId || isProcessing) return true;
    // Block save while a folder operation is in flight or open — but only when
    // the SharePoint picker is actually visible. When hidden, stale busy flags
    // shouldn't gate the save (e.g. neither checkbox ticked → DB-only save).
    if (isFolderPickerVisible() && (isLoadingSharePoint || isRefreshingFolders || isFolderOpBusy)) return true;
    // Any checkbox ticked requires a SharePoint upload, so the client must have a valid site.
    if ((saveEmailChecked || saveAttachmentsChecked) && !hasValidSharePointSite()) return true;
    // Both unchecked = default action: save email only to portal. Always allowed.
    if (!saveEmailChecked && saveAttachmentsChecked && attachments.length === 0) return true;
    if (saveAttachmentsChecked && attachments.length > 0) {
      if (!attachments.some(att => att.selected)) return true;
    }
    return false;
  };

  // Show loading spinner for initial load
  if (isLoading) {
    return (
      <div className="app-container">
        <LoadingOverlay text="E-mail laden..." />
      </div>
    );
  }

  // If there's an auth error, show the unauthorized screen
  if (authError) {
    return (
      <UnauthorizedAccess
        errorMessage={authError.message}
        errorType={authError.type}
        onRetry={handleAuthRetry}
      />
    );
  }

  return (
    <div className="app-container">
      <Header emailInfo={emailInfo} isOutgoingEmail={isOutgoingEmail} />

      <main className="main-content">
        {message && (
          <MessageBanner
            message={message}
            isFading={isMessageFading}
            onDismiss={dismissMessage}
          />
        )}

        <ClientSelector
          clients={clients}
          selectedClientId={selectedClientId}
          onChange={setSelectedClientId}
          disabled={isProcessing}
          allowCustomSearch={true}
          onCustomPick={handleCustomClientPick}
        />

        {isFolderPickerVisible() && (
          <SharePointFolderPicker
            folderTree={folderTree}
            selectedFolder={selectedFolder}
            expandedPaths={expandedPaths}
            sharepointFolderPath={sharepointFolderPath}
            documentsRootUrl={documentsRootUrl}
            isLoadingSharePoint={isLoadingSharePoint}
            isRefreshingFolders={isRefreshingFolders}
            isProcessing={isProcessing}
            hasValidSite={hasValidSharePointSite()}
            onTreeSelect={handleTreeSelect}
            onTreeToggle={handleTreeToggle}
            onCreateFolder={handleCreateFolder}
            onRenameFolder={handleRenameFolder}
            onDeleteFolder={handleDeleteFolder}
            onMoveFolder={handleMoveFolder}
            onRefreshFolders={handleRefreshFolders}
            onBusyChange={setIsFolderOpBusy}
          />
        )}

        {selectedClientId && (
          <SaveOptions
            saveEmailChecked={saveEmailChecked}
            saveAttachmentsChecked={saveAttachmentsChecked}
            isOutgoingEmail={isOutgoingEmail}
            attachmentCount={attachments.length}
            isProcessing={isProcessing}
            onToggleSaveEmail={() => setSaveEmailChecked(!saveEmailChecked)}
            onToggleSaveAttachments={() => setSaveAttachmentsChecked(!saveAttachmentsChecked)}
          />
        )}

        {saveAttachmentsChecked && attachments.length > 0 && selectedClientId && !isOutgoingEmail && (
          <AttachmentsList
            attachments={attachments}
            isProcessing={isProcessing}
            onToggle={toggleAttachment}
            onToggleAll={toggleAllAttachments}
          />
        )}

        {emailInfo && emailInfo.attachmentCount === 0 && selectedClientId && !isOutgoingEmail && (
          <div className="info-banner">
            <span className="material-icons-round banner-icon">info</span>
            <div className="banner-content">
              <p className="banner-text">Deze e-mail heeft geen bijlagen om te uploaden.</p>
            </div>
          </div>
        )}

        {isOutgoingEmail && (
          <div className="info-box">
            <span className="material-icons-round info-box-icon">info</span>
            <p className="info-box-text">
              Dit is een uitgaande e-mail. Deze wordt opgeslagen bij de klant.
            </p>
          </div>
        )}
      </main>

      <footer className="footer-actions">
        <ActionButtons
          isProcessing={isProcessing}
          isSaveDisabled={isSaveDisabled()}
          onSave={handleSaveEmail}
          onCancel={handleCancel}
        />
      </footer>

      {isProcessing && <LoadingOverlay text="E-mail wordt opgeslagen..." />}
    </div>
  );
};

export default App;
